import React, { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Phone, PhoneCall, Mic, MicOff, User } from 'lucide-react';
import { PhoneTransfer, Chat, Envelope, WebhooksLogo, Database, AndroidLogo } from '@phosphor-icons/react';
import { cn } from '../../lib/utils';
import useVapi from '../../hooks/use-vapi';
import { generateCurrentVapiJWT, updateAssistant } from '../../utils/vapiAuth';
import Vapi from '@vapi-ai/web';
import { getSupabaseClient } from '../../supabaseClient';
import { API_BASE_URL, endpoints } from '../../config/api';
import { useBusinessType } from '../../context/BusinessContext';
import { Input } from '../ui/input';

// Helpers for phone number formatting
const formatPhoneNumberForDisplay = (value) => {
  if (!value) return value;
  const phoneNumber = value.replace(/[^\d]/g, '').slice(0, 10);
  const length = phoneNumber.length;

  if (length < 4) return `(${phoneNumber}`;
  if (length < 7) return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};

const formatPhoneNumberToE164 = (value) => {
  const digits = value.replace(/[^\d]/g, '');
  if (digits.length === 10) {
    return `+1${digits}`;
  }
  return null;
};

// Simple Avatar components (adapted from the vapi blocks) - Updated to match RoadsPopup style
const Avatar = ({ className, children, ...props }) => (
  <div
    className={cn(
      "p-1.5 bg-gray-100/80 rounded-lg flex items-center justify-center",
      className
    )}
    {...props}
  >
    {children}
  </div>
);

const AvatarImage = ({ className, src, alt, ...props }) => (
  <img
    className={cn("aspect-square h-full w-full rounded-lg object-cover", className)}
    src={src}
    alt={alt}
    {...props}
  />
);

const AvatarFallback = ({ className, children, ...props }) => (
  <div
    className={cn(
      "flex h-full w-full items-center justify-center rounded-lg text-gray-700",
      className
    )}
    {...props}
  >
    {children}
  </div>
);

// Transcriber component (adapted from vapi blocks)
const Transcriber = ({ conversation }) => {
  const scrollRef = useRef(null);
  const [toolCallStates, setToolCallStates] = useState(new Map());

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [conversation]);

  // Get tool call configuration (icon, colors, etc.)
  const getToolCallConfig = (toolName) => {
    // Extract action type from tool name (e.g., "sendDirections_Revere" -> SMS action)
    if (toolName.includes('sendDirections') || toolName.includes('sms')) {
      return {
        icon: Chat,
        bubbleColor: 'bg-amber-100 text-amber-700 border-amber-200',
        iconColor: 'text-amber-600',
        avatarColor: '#fef3c7',
        type: 'SMS'
      };
    } else if (toolName.includes('email')) {
      return {
        icon: Envelope,
        bubbleColor: 'bg-amber-100 text-amber-700 border-amber-200',
        iconColor: 'text-amber-600',
        avatarColor: '#fef3c7',
        type: 'Email'
      };
    } else if (toolName.includes('transfer') || toolName.includes('call')) {
      return {
        icon: PhoneTransfer,
        bubbleColor: 'bg-amber-100 text-amber-700 border-amber-200',
        iconColor: 'text-amber-600',
        avatarColor: '#fef3c7',
        type: 'Transfer'
      };
    } else if (toolName.includes('api') || toolName.includes('webhook')) {
      return {
        icon: WebhooksLogo,
        bubbleColor: 'bg-amber-100 text-amber-700 border-amber-200',
        iconColor: 'text-amber-600',
        avatarColor: '#fef3c7',
        type: 'API'
      };
    } else {
      // Default to complex answer for unknown tools
      return {
        icon: Database,
        bubbleColor: 'bg-purple-100 text-purple-700 border-purple-200',
        iconColor: 'text-purple-600',
        avatarColor: '#f3e8ff',
        type: 'Complex'
      };
    }
  };

  // Group consecutive messages from the same speaker, but handle tool calls specially
  const groupedConversation = conversation.reduce((groups, message) => {
    const lastGroup = groups[groups.length - 1];

    // Don't group tool calls - they need special handling
    if (message.role === 'tool_calls' || message.role === 'tool_call_result') {
      groups.push({
        role: message.role,
        text: message.text,
        timestamp: message.timestamp,
        toolCalls: message.toolCalls,
        result: message.result,
        toolCallId: message.toolCallId
      });
    } else if (lastGroup && lastGroup.role === message.role && message.role !== 'tool_calls' && message.role !== 'tool_call_result') {
      // Same speaker - combine messages with a space
      lastGroup.text += ' ' + message.text;
    } else {
      // Different speaker or first message - create new group
      groups.push({
        role: message.role,
        text: message.text,
        timestamp: message.timestamp
      });
    }

    return groups;
  }, []);

  // Track tool call states for animations with minimum 3-second delay
  useEffect(() => {
    const processedToolCalls = new Set();

    groupedConversation.forEach((message, index) => {
      if (message.role === 'tool_calls' && message.toolCalls) {
        message.toolCalls.forEach(toolCall => {
          if (!processedToolCalls.has(toolCall.id)) {
            processedToolCalls.add(toolCall.id);

            setToolCallStates(prevStates => {
              if (!prevStates.has(toolCall.id)) {
                const newStates = new Map(prevStates);
                newStates.set(toolCall.id, {
                  status: 'loading',
                  toolCall,
                  index,
                  startTime: Date.now()
                });
                return newStates;
              }
              return prevStates;
            });
          }
        });
      } else if (message.role === 'tool_call_result' && message.toolCallId) {
        if (!processedToolCalls.has(`result_${message.toolCallId}`)) {
          processedToolCalls.add(`result_${message.toolCallId}`);

          setToolCallStates(prevStates => {
            const existingState = prevStates.get(message.toolCallId);
            if (existingState && existingState.status === 'loading' && !existingState.pendingResult) {
              const elapsedTime = Date.now() - existingState.startTime;
              const remainingTime = Math.max(0, 3000 - elapsedTime); // Minimum 3 seconds

              const finalStatus = message.result === 'Success.' ? 'success' : 'failure';
              const newStates = new Map(prevStates);

              if (remainingTime > 0) {
                // Store the result but don't show it immediately
                newStates.set(message.toolCallId, {
                  ...existingState,
                  pendingResult: finalStatus,
                  result: message.result,
                  remainingTime
                });

                // Show the result after the minimum time has passed
                setTimeout(() => {
                  setToolCallStates(currentStates => {
                    const updated = new Map(currentStates);
                    const state = updated.get(message.toolCallId);
                    if (state && state.pendingResult) {
                      updated.set(message.toolCallId, {
                        ...state,
                        status: state.pendingResult,
                        pendingResult: null
                      });
                    }
                    return updated;
                  });
                }, remainingTime);
              } else {
                // Show immediately if 3 seconds have already passed
                newStates.set(message.toolCallId, {
                  ...existingState,
                  status: finalStatus,
                  result: message.result
                });
              }

              return newStates;
            }
            return prevStates;
          });
        }
      }
    });
  }, [groupedConversation]);

  return (
    <div className="flex flex-col h-full bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
      <div className="bg-gray-50 px-5 py-4 flex items-center justify-between border-b border-gray-100">
        <div className="font-medium text-gray-900 text-sm">Live Transcript</div>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {groupedConversation.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <PhoneTransfer className="w-6 h-6 text-gray-300 mb-3" />
            <p className="text-gray-500 text-sm">Start a call to see the conversation</p>
            <p className="text-xs text-gray-400 mt-1">Your AI responses will appear here</p>
          </div>
        ) : (
          groupedConversation.map((message, index) => {
            // Handle tool calls with special animated UI
            if (message.role === 'tool_calls' && message.toolCalls) {
              return message.toolCalls.map((toolCall, toolIndex) => {
                const config = getToolCallConfig(toolCall.function.name);
                const toolState = toolCallStates.get(toolCall.id);
                const isLoading = toolState?.status === 'loading' || toolState?.pendingResult;

                return (
                  <motion.div 
                    key={`${index}-${toolIndex}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05, duration: 0.3 }}
                    className="flex items-start gap-3"
                  >
                    {/* Consistent Icon Avatar - Never Changes Color */}
                    <div 
                      className="w-7 h-7 shrink-0 p-1.5 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: config.avatarColor }}
                    >
                      <config.icon className={cn("w-4 h-4", config.iconColor)} />
                    </div>

                    {/* Consistent Tool Call Bubble - Never Changes Color */}
                    <motion.div 
                      className={cn(
                        "px-3 py-2 rounded-xl max-w-[75%] shadow-sm border",
                        config.bubbleColor
                      )}
                      animate={{ 
                        scale: isLoading ? [1, 1.02, 1] : 1
                      }}
                      transition={{ 
                        scale: { duration: 1.5, repeat: isLoading ? Infinity : 0 }
                      }}
                    >
                      <div className="flex items-center space-x-2 mb-1">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{toolCall.function.name}</div>
                          <div className="text-xs text-gray-500 mt-0.5">{config.type}</div>
                        </div>
                      </div>

                      <div className="text-xs font-light">
                        {/* Status Text - This is the ONLY part that changes color */}
                        {isLoading && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="flex items-center text-xs opacity-70"
                          >
                            {/* Apple iMessage-style typing dots */}
                            <div className="flex space-x-1">
                              <motion.div
                                className="w-1.5 h-1.5 bg-current rounded-full"
                                animate={{ 
                                  scale: [1, 1.5, 1],
                                  opacity: [0.5, 1, 0.5]
                                }}
                                transition={{ 
                                  duration: 1.4,
                                  repeat: Infinity,
                                  delay: 0,
                                  ease: "easeInOut"
                                }}
                              />
                              <motion.div
                                className="w-1.5 h-1.5 bg-current rounded-full"
                                animate={{ 
                                  scale: [1, 1.5, 1],
                                  opacity: [0.5, 1, 0.5]
                                }}
                                transition={{ 
                                  duration: 1.4,
                                  repeat: Infinity,
                                  delay: 0.2,
                                  ease: "easeInOut"
                                }}
                              />
                              <motion.div
                                className="w-1.5 h-1.5 bg-current rounded-full"
                                animate={{ 
                                  scale: [1, 1.5, 1],
                                  opacity: [0.5, 1, 0.5]
                                }}
                                transition={{ 
                                  duration: 1.4,
                                  repeat: Infinity,
                                  delay: 0.4,
                                  ease: "easeInOut"
                                }}
                              />
                            </div>
                            <span className="ml-2">Executing action</span>
                          </motion.div>
                        )}

                        {toolState?.status === 'success' && (
                          <motion.div
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-xs text-green-600 font-medium"
                          >
                            Action completed successfully
                          </motion.div>
                        )}

                        {toolState?.status === 'failure' && (
                          <motion.div
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-xs text-red-600 font-medium"
                          >
                            Action failed: {toolState.result}
                          </motion.div>
                        )}
                      </div>

                      <div className="text-xs opacity-70 mt-1.5 font-light">{message.timestamp}</div>
                    </motion.div>
                  </motion.div>
                );
              });
            }

            // Skip tool_call_result messages since they're handled above
            if (message.role === 'tool_call_result') {
              return null;
            }

            // Handle regular messages
            return (
              <motion.div 
                key={index} 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05, duration: 0.3 }}
                className={`flex items-start gap-3 ${message.role === 'user' ? 'justify-end' : ''}`}
              >
                {message.role === 'assistant' && (
                  <Avatar className="w-7 h-7 shrink-0">
                    <AvatarFallback>
                      <AndroidLogo className="w-4 h-4 text-gray-700" />
                    </AvatarFallback>
                  </Avatar>
                )}
                <div className={cn(
                  "px-3 py-2 rounded-xl max-w-[75%] shadow-sm",
                  message.role === 'user' 
                    ? "bg-black text-white" 
                    : "bg-gray-100 text-gray-900 border border-gray-200"
                )}>
                  <p className="text-sm font-light leading-relaxed">{message.text}</p>
                  <div className="text-xs opacity-70 mt-1.5 font-light">{message.timestamp}</div>
                </div>
                {message.role === 'user' && (
                  <Avatar className="w-7 h-7 shrink-0 bg-black">
                    <AvatarFallback>
                      <User className="w-4 h-4 text-white" />
                    </AvatarFallback>
                  </Avatar>
                )}
              </motion.div>
            );
          }).filter(Boolean)
        )}
      </div>
    </div>
  );
};

const AITestModal = ({ isOpen, onClose, categories = [] }) => {
  const { businessType } = useBusinessType();
  const supabase = getSupabaseClient(businessType);

  const [conversation, setConversation] = useState([]);
  const [isCallActive, setIsCallActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [volumeLevel, setVolumeLevel] = useState(0);
  const [callerNumber, setCallerNumber] = useState('');

  const vapiRef = useRef(null);
  const authDataRef = useRef(null);

  // Get phone number from database
  const getPhoneNumber = useCallback(async () => {
    try {
      const { getUserAccountId } = await import('../../utils/accountUtils');
      const { accountId, error: accountIdError } = await getUserAccountId();
      if (accountIdError || !accountId) {
        throw new Error('Account ID not found');
      }

      const { data, error } = await supabase
        .from('vapi_call_settings')
        .select('phone_number')
        .eq('account_id', accountId)
        .maybeSingle();

      if (error) throw error;
      if (!data?.phone_number) {
        throw new Error('Phone number not found in settings');
      }

      return data.phone_number;
    } catch (err) {
      console.error('Error getting phone number:', err);
      throw err;
    }
  }, [supabase]);

  // Get squad configuration from backend
  const getSquadConfiguration = useCallback(async (phoneNumber, accountId, customerNumber) => {
    try {
      const requestBody = {
        message: {
          type: "assistant-request",
          phoneNumber: {
            number: phoneNumber
          },
          customer: {
            number: customerNumber || phoneNumber
          }
        },
        call: {
          metadata: {
            accountId: accountId
          }
        }
      };

      const response = await fetch(`${API_BASE_URL}${endpoints.vapiSquad(phoneNumber)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`Failed to get squad configuration: ${response.status}`);
      }

      const squadData = await response.json();
      return squadData;
    } catch (err) {
      console.error('Error getting squad configuration:', err);
      throw err;
    }
  }, []);

  // Initialize Vapi instance
  const initializeVapi = useCallback(async () => {
    if (vapiRef.current) return;

    try {
      const authData = await generateCurrentVapiJWT();
      if (!authData?.token) {
        throw new Error('Failed to get authentication token');
      }

      authDataRef.current = authData;
      vapiRef.current = new Vapi(authData.token);

      // Set up event listeners
      vapiRef.current.on('call-start', () => {
        console.log('AI Test call started');
        setIsCallActive(true);
        setIsLoading(false);
        setError(null);
      });

      vapiRef.current.on('call-end', () => {
        console.log('AI Test call ended');
        setIsCallActive(false);
        setIsLoading(false);
        setVolumeLevel(0);
      });

      vapiRef.current.on('volume-level', (volume) => {
        setVolumeLevel(volume);
      });

      vapiRef.current.on('error', (e) => {
        console.error('Vapi error:', e);
        setError(e.message || 'An error occurred during the call');
        setIsLoading(false);
        setIsCallActive(false);
      });

      vapiRef.current.on('message', (message) => {
        console.log('📨 Vapi message received:', message); // Debug log to see all message types

        if (message.type === 'transcript' && message.transcriptType === 'final') {
          setConversation((prev) => [
            ...prev,
            { 
              role: message.role, 
              text: message.transcript,
              timestamp: new Date().toLocaleTimeString(),
              isFinal: true
            }
          ]);
        } else if (message.type === 'tool-calls') {
          // Handle tool calls - the correct message type from Vapi
          console.log('🔧 Tool call detected:', message);
          setConversation((prev) => [
            ...prev,
            {
              role: 'tool_calls',
              text: '',
              toolCalls: message.toolCalls || [],
              timestamp: new Date().toLocaleTimeString()
            }
          ]);
        } else if (message.type === 'conversation-update') {
          // Check if conversation update contains tool call results
          const messages = message.messages || [];
          const lastMessage = messages[messages.length - 1];

          if (lastMessage && lastMessage.role === 'tool_call_result') {
            console.log('✅ Tool result detected in conversation update:', lastMessage);
            setConversation((prev) => [
              ...prev,
              {
                role: 'tool_call_result',
                text: '',
                result: lastMessage.result || 'Function executed',
                toolCallId: lastMessage.toolCallId,
                timestamp: new Date().toLocaleTimeString()
              }
            ]);
          }
        }
      });

    } catch (err) {
      console.error('Error initializing Vapi:', err);
      setError('Failed to initialize voice AI');
    }
  }, []);

  // Start call
  const startCall = useCallback(async () => {
    if (!vapiRef.current || !authDataRef.current) {
      await initializeVapi();
    }

    if (isCallActive || isLoading) return;

    try {
      setIsLoading(true);
      setError(null);
      setConversation([]);

      const e164Number = formatPhoneNumberToE164(callerNumber);
      if (!e164Number) {
        throw new Error('Please enter a valid 10-digit phone number to start the call.');
      }

      // Get account ID and phone number from database
      console.log('Getting account ID and phone number from database...');
      const { getUserAccountId } = await import('../../utils/accountUtils');
      const { accountId, error: accountIdError } = await getUserAccountId();
      if (accountIdError || !accountId) {
        throw new Error('Account ID not found');
      }

      const phoneNumber = await getPhoneNumber();

      // Get squad configuration from backend
      console.log('Getting squad configuration for phone:', phoneNumber);
      const squadResponse = await getSquadConfiguration(phoneNumber, accountId, e164Number);

      console.log('Squad configuration received:', squadResponse);

      // For testing, Vapi expects a single assistant object directly if not using a full squad setup.
      // Extract the first assistant from the squad members to reduce payload size.
      const testAssistantConfig = squadResponse?.messageResponse?.squad?.members[0]?.assistant;

      if (!testAssistantConfig) {
        throw new Error('Invalid or empty squad configuration received from backend (no first assistant found)');
      }

      console.log('Starting AI test call with single assistant:', testAssistantConfig.name);
      await vapiRef.current.start(testAssistantConfig);

    } catch (err) {
      console.error('Failed to start call:', err);
      setError(err.message || 'Failed to start call');
      setIsLoading(false);
    }
  }, [isCallActive, isLoading, categories, initializeVapi, getPhoneNumber, getSquadConfiguration, callerNumber]);

  // Stop call
  const stopCall = useCallback(() => {
    if (vapiRef.current) {
      vapiRef.current.stop();
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (vapiRef.current) {
        vapiRef.current.stop();
        vapiRef.current = null;
      }
    };
  }, []);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setConversation([]);
      setIsCallActive(false);
      setIsLoading(false);
      setError(null);
      if (vapiRef.current) {
        vapiRef.current.stop();
      }
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="w-full h-full bg-white border-l border-gray-200/50 flex flex-col">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="flex-shrink-0 border-b border-gray-100 bg-white"
      >
        <div className="flex items-center justify-between px-5 py-5">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gray-100/80 rounded-lg">
              <PhoneTransfer className="w-4 h-4 text-gray-700" />
            </div>
            <div>
              <h2 className="font-medium text-gray-900 text-lg">Test Your AI</h2>
              <p className="text-xs text-gray-500 mt-0.5">Have a voice conversation to test your AI</p>
            </div>
          </div>
          <motion.button
            whileHover={{ scale: 1.05, backgroundColor: '#f3f4f6' }}
            whileTap={{ scale: 0.95 }}
            onClick={onClose}
            className="rounded-full h-8 w-8 flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </motion.button>
        </div>

        {/* Call Controls */}
        <div className="px-5 pb-5 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={isCallActive ? stopCall : startCall}
                disabled={isLoading}
                className={cn(
                  "flex items-center space-x-2 px-4 py-2.5 rounded-xl font-medium text-sm transition-all",
                  isCallActive 
                    ? "bg-red-100 text-red-700 hover:bg-red-200" 
                    : "bg-black text-white hover:bg-gray-800",
                  isLoading && "opacity-50 cursor-not-allowed"
                )}
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    <span>Starting...</span>
                  </>
                ) : isCallActive ? (
                  <>
                    <Phone className="w-4 h-4" />
                    <span>End Call</span>
                  </>
                ) : (
                  <>
                    <PhoneCall className="w-4 h-4" />
                    <span>Start Call</span>
                  </>
                )}
              </motion.button>

              {/* Volume Indicator */}
              {isCallActive && (
                <div className="flex items-center space-x-2">
                  <Mic className="w-4 h-4 text-gray-400" />
                  <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-green-500 rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.min(volumeLevel * 100, 100)}%` }}
                      transition={{ duration: 0.1 }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Status */}
            <div className="text-xs text-gray-500 font-light">
              {isCallActive ? 'Call Active' : (
                <div className="flex items-center space-x-2">
                  <Input
                    type="tel"
                    placeholder="(555) 555-5555"
                    value={callerNumber}
                    onChange={(e) => setCallerNumber(formatPhoneNumberForDisplay(e.target.value))}
                    className="w-36 h-8 text-xs px-2 py-1 border-gray-300 rounded-md focus:ring-1 focus:ring-black/50"
                    disabled={isLoading || isCallActive}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Error Message */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mx-5 mt-4 p-3 bg-red-50 border border-red-200 rounded-xl"
        >
          <p className="text-sm text-red-700 font-light">{error}</p>
        </motion.div>
      )}

      {/* Transcriber */}
      <div className="flex-1 p-3 overflow-hidden">
        <Transcriber conversation={conversation} />
      </div>
    </div>
  );
};

export default AITestModal; 